"use client";

import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { LicensePlate } from "@offstreetparking/design-system/dataDisplay/LicensePlate";
import { FormError } from "@offstreetparking/design-system/forms";
import { AddIcon } from "@offstreetparking/design-system/icons/AddIcon";
import { ChevronRightIcon } from "@offstreetparking/design-system/icons/ChevronRightIcon";
import { useFormContext, useWatch } from "react-hook-form";
import { type ParkerVehicle, vehicleKey } from "./parker-vehicle";

type ParkingDetailsFormValues = {
	vehicles: ParkerVehicle[];
};

type SelectedVehiclesSummaryProps = {
	maxVehicles: number;
	onOpenSelectModal: () => void;
};

export const SelectedVehiclesSummary = ({ maxVehicles, onOpenSelectModal }: SelectedVehiclesSummaryProps) => {
	const { t } = useLingui();
	const { control, formState } = useFormContext<ParkingDetailsFormValues>();
	const selectedVehicles = (
		useWatch({
			control,
			name: "vehicles",
		}) ?? []
	).filter((vehicle) => vehicle.plate);
	const vehiclesError =
		typeof formState.errors.vehicles?.message === "string" ? formState.errors.vehicles.message : undefined;
	return (
		<div className="space-y-4">
			{selectedVehicles.length === 0 ? (
				<button
					type="button"
					className="w-full rounded-lg border border-neutral-faded border-dashed p-6 text-center"
					onClick={onOpenSelectModal}
				>
					<span className="inline-flex items-center gap-2 font-medium text-accent">
						<AddIcon className="h-5 w-5" />
						{t`${plural(maxVehicles, {
							one: "Add Vehicle",
							other: "Add Vehicles",
						})}`}
					</span>
				</button>
			) : (
				<button
					type="button"
					className="flex w-full cursor-pointer items-center justify-between gap-4 rounded-lg border border-neutral bg-white p-3 text-left"
					onClick={onOpenSelectModal}
				>
					<span className="flex flex-wrap gap-4">
						{selectedVehicles.map((vehicle) => (
							<LicensePlate key={vehicleKey(vehicle)} plate={vehicle.plate} state={vehicle.state} />
						))}
					</span>
					<span className="inline-flex shrink-0 items-center gap-1 font-medium">
						<span className="underline">{t`Change`}</span>
						<ChevronRightIcon className="h-4 w-4" />
					</span>
				</button>
			)}

			{vehiclesError && <FormError error={vehiclesError} />}
		</div>
	);
};
